import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Stack, Box } from '@vapor/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AuditFragment, ChargeCategoryAuditFragment, ReservationStateFragment, VatAuditFragment, useReservedResourceAuditQuery } from 'src/api/api.generated'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import useGetContent from './get-content'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { Link } from '@mui/material'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import { isDateBetween } from 'src/utils/date'
import { displayResourceType } from 'src/utils/resource-type'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservationDetailDrawer } from '../../booking/reservation-detail/reservation-detail-wrapper'
import { DB_ENTITIES } from '../utils/translate-entity-name'

export default function ChangesDialog({
  open,
  onClose,
  audit,
  reservationStates,
  chargeCategories,
  vats,
  tenantId,
  propertyId,
}: {
  open: boolean
  onClose: () => void
  audit: AuditFragment
  reservationStates: ReservationStateFragment[]
  chargeCategories: ChargeCategoryAuditFragment[]
  vats: VatAuditFragment[]
  tenantId: string
  propertyId: string
}) {
  const { t } = useTranslation()
  const showSuccess = useSuccessSnackBar()
  const showError = useErrorSnackBar()
  const localizeString = useLocalizeString()
  const hotelDate = useHotelDate()
  const [showJSON, setShowJSON] = useState(false)
  const [openReservation, setOpenReservation] = useState(false)
  const changes_string = JSON.stringify(audit.changes, null, 2)
  const content = useGetContent({ audit, reservationStates, chargeCategories, vats })

  const reservedResourceId = audit.entityName === DB_ENTITIES.ReservedResource ? audit.changes?.id ?? null : audit?.changes?.reservedResourceId ?? null
  const { data } = useReservedResourceAuditQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: propertyId,
        },
        id: {
          eq: reservedResourceId,
        },
      },
    },
    skip: !reservedResourceId,
    onError: showError,
  })
  const reservedResource = data?.reservedResources?.[0] ?? null
  const resource = reservedResource?.resourceOccupancies.find((resourceOccupancy) => isDateBetween(hotelDate, resourceOccupancy.start, resourceOccupancy.end))

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle>{t('pms.audits.grid.changes')}</DialogTitle>
        <DialogContent sx={{ whiteSpace: 'pre-line' }}>
          <Stack gap={1} alignItems="start">
            {content !== null && content}
            {reservedResource !== null && (
              <Box marginTop={0.5}>
                {t('pms.audits.grid.changes.reserved_resource.reservation')}
                <Link
                  marginX={0.5}
                  sx={{ cursor: 'pointer', fontSize: '16px' }}
                  onClick={() => {
                    setOpenReservation(true)
                  }}
                >
                  {reservedResource.reservation.reservationCode}
                </Link>
                {t('pms.audits.grid.changes.reserved_resource.resource', {
                  resource: resource?.resource?.code ?? '',
                  resourceType: resource?.reservedResourceType ? displayResourceType(localizeString, resource.reservedResourceType) : '',
                })}
              </Box>
            )}
            {(content !== null || reservedResource !== null) && (
              <Button
                variant="text"
                sx={{ display: 'block' }}
                onClick={() => {
                  setShowJSON(!showJSON)
                }}
              >
                {t('pms.audits.grid.changes.show_json')}
              </Button>
            )}
            {(content === null || showJSON) && (
              <>
                {(content !== null || reservedResource !== null) && <Divider light flexItem />}
                <Button
                  variant="text"
                  onClick={() => {
                    navigator.clipboard.writeText(changes_string)
                    showSuccess(t('commons.copied_to_clipboard'))
                  }}
                >
                  {t('commons.copy_to_clipboard')}
                </Button>
                <pre>{changes_string}</pre>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            {t('commons.close')}
          </Button>
        </DialogActions>
      </Dialog>
      {reservedResource !== null && (
        <ReservationDetailDrawer
          propertyId={propertyId}
          reservationId={reservedResource.reservation.id}
          open={openReservation}
          onClose={() => {
            setOpenReservation(false)
          }}
        />
      )}
    </>
  )
}
